/**
 * Storage model: named collections of records with a string `id`, behind a small `Backend` interface, plus a schema
 * version with data migrations. The app uses the IndexedDB backend (`idb.ts`); tests use `MemoryBackend`.
 *
 * Every change to the stored shape of a record raises the schema version by adding a migration that rewrites the old
 * records, so data saved by an older build keeps loading. Never remove or reorder migrations.
 */

/** The collections the app stores. Adding one needs no migration (backends create missing collections). */
export const COLLECTIONS = ["exercises", "templates", "sessions"] as const;
export type Collection = (typeof COLLECTIONS)[number];

/**
 * Motion recordings (`StoredRecording` in motion.ts, a few MB each): test data for the rep detection, exported on
 * their own from the recording screen. Kept out of `COLLECTIONS` so backups and restores leave them alone.
 */
export const RECORDINGS = "recordings";

/** Internal collection holding the schema version (record id "schema"). */
export const META = "meta";
/**
 * Record id in META of the workout in progress (`{ id, workout }`, see workout.ts). It is saved on every change so a
 * workout survives closing the app. A migration that changes the shape of sessions must migrate this record too.
 */
export const ACTIVE_WORKOUT = "activeWorkout";

export type StoredRecord = { id: string };

export interface Backend {
  getAll<T extends StoredRecord>(collection: string): Promise<T[]>;
  get<T extends StoredRecord>(collection: string, id: string): Promise<T | undefined>;
  put(collection: string, record: StoredRecord): Promise<void>;
  delete(collection: string, id: string): Promise<void>;
}

export interface Migration {
  /** The schema version the data has after this migration. */
  to: number;
  run(backend: Backend): Promise<void>;
}

/** Version 1 is the first stored shape; migrations start at version 2. */
export const MIGRATIONS: Migration[] = [
  // 2 (todo 16): template, workout and session entries may carry `measure` ("reps" | "timed", the exercise's variant).
  // Old records are valid as they are; the new version keeps older builds from reading (and dropping) the field.
  { to: 2, run: async () => {} },
];

const latest = (migrations: Migration[]) => migrations.reduce((v, m) => Math.max(v, m.to), 1);
export const SCHEMA_VERSION = latest(MIGRATIONS);

type SchemaRecord = StoredRecord & { version: number };
const schemaRecord = (version: number): SchemaRecord => ({ id: "schema", version });

/**
 * Brings the stored data up to the last version in `migrations` and returns the backend. An empty database is
 * stamped with the current version. Data from a newer build is refused rather than misread.
 */
export async function openStorage(backend: Backend, migrations: Migration[] = MIGRATIONS): Promise<Backend> {
  const current = latest(migrations);
  const stored = (await backend.get<SchemaRecord>(META, "schema"))?.version;
  if (stored === undefined) {
    await backend.put(META, schemaRecord(current));
    return backend;
  }
  if (stored > current) throw new Error(`The stored data has schema version ${stored}, this app only knows up to ${current}. Install the newer app.`);
  for (const m of [...migrations].sort((a, b) => a.to - b.to)) {
    if (m.to <= stored) continue;
    await m.run(backend);
    // stamped after each step, so an interrupted run continues at the step that failed
    await backend.put(META, schemaRecord(m.to));
  }
  return backend;
}

/** In-memory backend for tests. Records are copied in and out, like IndexedDB's structured clone. */
export class MemoryBackend implements Backend {
  readonly data = new Map<string, Map<string, StoredRecord>>();
  private col(name: string) {
    let c = this.data.get(name);
    if (!c) this.data.set(name, (c = new Map()));
    return c;
  }
  async getAll<T extends StoredRecord>(collection: string) {
    return [...this.col(collection).values()].map((r) => structuredClone(r) as T);
  }
  async get<T extends StoredRecord>(collection: string, id: string) {
    const r = this.col(collection).get(id);
    return r && (structuredClone(r) as T);
  }
  async put(collection: string, record: StoredRecord) {
    this.col(collection).set(record.id, structuredClone(record));
  }
  async delete(collection: string, id: string) {
    this.col(collection).delete(id);
  }
}

/** A random id for a new record (never derived from a name, so renaming keeps history and imports match). */
export function newId(): string {
  return crypto.randomUUID();
}
